import { slicedToArray as _slicedToArray, createForOfIteratorHelper as _createForOfIteratorHelper } from '../_virtual/_rollupPluginBabelHelpers.js';
import { cookiesToZUID, cookiesToZGUID } from '@zillow/mortgage-urls';
import { useState, useEffect } from 'react';

/**
 * To avoid hydration error, we initialize this with what is given. And then on
 * mount, 2nd render, we populate it. This allows taking in a undefined or null
 * because hooks cannot be conditionally called. So we accept the undefined or
 * null, but we just don't run populateParamPlaceholders.
 *
 */
function usePopulateParamPlaceHolders(stringWithParamPlaceholders) {
  var _useState = useState(stringWithParamPlaceholders),
    _useState2 = _slicedToArray(_useState, 2),
    populatedParams = _useState2[0],
    setPopulatedParams = _useState2[1];
  useEffect(function populateParamsOnMountToAvoidHydrationError() {
    if (!stringWithParamPlaceholders) {
      return;
    }
    setPopulatedParams(populateParamPlaceholders(stringWithParamPlaceholders));
  }, [stringWithParamPlaceholders]);
  return populatedParams;
}

/**
 * Populates empty URL parameter placeholders with values from the current page
 * URL or from cookies for special parameters (zuid and guid). If the current
 * page URL or cookies don't have a value but there is a placeholder for it in
 * the input, then it remains blank.
 *
 * @example
 * ```ts
 * Current Page: https://example.com/?param1=value1&param3=value3
 *
 * populateParamPlaceholders('/?param1=) -> /?param1=value1
 * populateParamPlaceholders('/?param2=) -> /?param2=
 * populateParamPlaceholders('/?param3=&param1=) -> /?param3=value3&param1=value1
 * ```
 *
 * @param inputString - URL like string with params with some of them being empty which means they are placeholders. Non-strings are returned as-is.
 *
 * @returns stringWithPopulatedParams The URL with populated parameter values where possible
 */
function populateParamPlaceholders(inputString) {
  if (!inputString) {
    return inputString;
  }
  if (typeof window === 'undefined') {
    // This can only run in the browser
    return inputString;
  }
  if (typeof inputString !== 'string') {
    return inputString;
  }

  // This will get mutated below.
  var inputSearchParams;
  try {
    inputSearchParams = new URL(inputString, window.location.origin).searchParams;
  } catch (error) {
    console.error('Error creating URL object from inputString', error);
    return inputString;
  }

  // This is the original search string from the input before mutations.
  var originalInputSearchString = '?' + inputSearchParams.toString();

  // Get current page parameters
  var currentPageParams = new URL(window.location.href, window.location.origin).searchParams;

  // zuid and guid are from cookies only. if it is in the current page params,
  // we override with blank.
  currentPageParams.set('zuid', ifNullStringNull(cookiesToZUID(document.cookie)) || '');
  currentPageParams.set('guid', ifNullStringNull(cookiesToZGUID(document.cookie)) || '');
  for (var _i = 0, _arr = Array.from(inputSearchParams.entries()); _i < _arr.length; _i++) {
    var _arr$_i = _slicedToArray(_arr[_i], 2),
      maybePlaceholderParam = _arr$_i[0],
      maybePlaceholderParamValue = _arr$_i[1];
    var isPlaceholderParam = maybePlaceholderParamValue === '';

    // If the parameter is empty in the original URL and has a value in currentPageParams
    if (isPlaceholderParam && currentPageParams.has(maybePlaceholderParam)) {
      // Get all values for this parameter from currentPageParams
      var currentPageValues = currentPageParams.getAll(maybePlaceholderParam);

      // Delete the existing empty parameter
      inputSearchParams.delete(maybePlaceholderParam);

      // Add all values from currentPageParams
      var _iterator = _createForOfIteratorHelper(currentPageValues),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var value = _step.value;
          if (!value.trim()) {
            // It's in currentPageParams, but it's empty. So we skip it.
            continue;
          }
          inputSearchParams.append(maybePlaceholderParam, value);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
  }

  // Use Array.from() otherwise deleting during iteration modifies the iterator
  Array.from(inputSearchParams.keys()).forEach(function (key) {
    if (inputSearchParams.get(key) === '') {
      inputSearchParams.delete(key);
    }
  });

  // if passed in /url?& doing new URL() on this and .toString() leaves the trailing ?& so this append and delete fixes that.
  var populatedInputSearchString = inputSearchParams.toString() === '' ? '' : '?' + inputSearchParams.toString();
  var result = inputString.replace(originalInputSearchString, populatedInputSearchString);
  return result;
}
function ifNullStringNull(value) {
  if (value === 'null') {
    return null;
  }
  return value;
}

export { populateParamPlaceholders, usePopulateParamPlaceHolders };
